import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getMovieById, getRelatedMovies } from '@/data/catalog';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import ShareButton from '@/pages/detail/components/ShareButton';

export default function Detail() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);

  useDocumentMeta({
    title: movie ? `${movie.title} - 大雷影视详情介绍` : '影视内容不存在 - 大雷影视',
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）是一部${movie.genres.join('、')}作品，由${movie.director}执导，${movie.cast.join('、')}主演。${movie.summary}`
      : '该影视内容不存在或已下架，请返回大雷影视首页浏览最新电影与电视剧。',
    keywords: movie
      ? `${movie.title},大雷影视,${movie.genres.join(',')},${movie.region}影视,${movie.year}影视`
      : '大雷影视,影视详情',
  });

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8">
        <EmptyState
          title="该影视内容不存在"
          description="这部作品的资料可能已被移除，或者链接有误。你可以在分类页或搜索页继续找找看。"
          actionLabel="返回首页"
          actionPath="/"
        />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 5);
  const categoryPath = movie.category === 'movie' ? '/category/movie' : '/category/tv';
  const categoryLabel = movie.category === 'movie' ? '电影' : '电视剧';
  const regionPath =
    ['中国大陆', '中国香港', '中国台湾'].includes(movie.region) ? '/category/guochan' : '/category/oumei';

  const metaRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join(' / ') },
    { label: '年份', value: `${movie.year}` },
    { label: '地区', value: movie.region },
    { label: '类型', value: movie.genres.join(' / ') },
    { label: '片长', value: movie.duration },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop || movie.poster}
            alt=""
            aria-hidden
            className="h-full w-full scale-105 object-cover opacity-25 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/60" />
        </div>

        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb
            items={[
              { label: `${categoryLabel}分类`, path: categoryPath },
              { label: movie.title },
            ]}
            light
          />

          <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:gap-8 md:mt-8">
            <div className="w-[150px] shrink-0 overflow-hidden rounded-lg border border-background-50/15 bg-background-900 sm:w-[200px] md:w-[240px]">
              <div className="relative aspect-[2/3] w-full">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 正式海报`}
                  title={`${movie.title} 大雷影视详情介绍`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                  {categoryLabel}
                </span>
                <span className="rounded-md border border-background-50/25 px-2 py-0.5 text-[11px] text-background-200">
                  {movie.year}
                </span>
                <span className="rounded-md border border-background-50/25 px-2 py-0.5 text-[11px] text-background-200">
                  {movie.region}
                </span>
                {movie.featured && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-background-50/10 px-2 py-0.5 text-[11px] text-accent-300">
                    <i className="ri-fire-fill" />
                    本站焦点
                  </span>
                )}
              </div>

              <h1 className="mt-3 text-2xl font-black leading-tight tracking-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-1.5 font-label text-sm uppercase tracking-[0.2em] text-background-300/70">
                {movie.subtitle}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-label text-4xl font-semibold leading-none text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-background-400">/ 10</span>
                </div>
                <div className="h-8 w-px bg-background-50/15" />
                <div className="flex flex-wrap items-center gap-1.5">
                  {movie.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/search?q=${encodeURIComponent(genre)}`}
                      className="cursor-pointer rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-200 transition-colors hover:bg-accent-500 hover:text-background-50"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200/90">{movie.summary}</p>

              <dl className="mt-6 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-10 shrink-0 text-background-400">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-background-100">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={categoryPath}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-circle-line text-base" />
                  查看同分类作品
                </Link>
                <ShareButton movie={movie} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-2">
            <section>
              <div className="flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold tracking-tight text-foreground-950">剧情介绍</h2>
              </div>
              <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px]">{movie.synopsis}</p>
              <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px]">
                《{movie.title}》是一部{movie.year}年上映的{movie.region}
                {movie.genres.join('、')}作品，{movie.duration.startsWith('全') ? `共${movie.duration.replace('全', '')}` : `片长${movie.duration}`}
                ，由{movie.director}执导，{movie.cast.join('、')}主演。片中延续了导演一贯克制的叙事节奏，把镜头更多留给人物之间沉默的片刻。
              </p>
            </section>

            <section className="mt-10">
              <div className="flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold tracking-tight text-foreground-950">所属分类</h2>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={categoryPath}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5 text-sm text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-folder-line" />
                  {categoryLabel}分类
                </Link>
                <Link
                  to={regionPath}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  <i className="ri-map-pin-line" />
                  {regionPath === '/category/guochan' ? '国产剧专区' : '欧美剧专区'}
                </Link>
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-background-200"
                  >
                    <i className="ri-price-tag-3-line" />
                    {genre}
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="h-5 w-1 rounded-full bg-accent-500" />
                  <h2 className="text-xl font-bold tracking-tight text-foreground-950">相关推荐</h2>
                </div>
                <Link
                  to={categoryPath}
                  className="inline-flex cursor-pointer items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-primary-600"
                >
                  更多同类作品
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-sm font-medium text-foreground-950">作品资料</h3>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.cast.join(' / ') },
                  { label: '上映年份', value: `${movie.year} 年` },
                  { label: '制片地区', value: movie.region },
                  { label: '类型', value: movie.genres.join(' / ') },
                  { label: movie.category === 'movie' ? '片长' : '集数', value: movie.duration },
                  { label: '作品编号', value: movie.id.toUpperCase() },
                ].map((row) => (
                  <div key={row.label} className="flex flex-col gap-1 border-b border-background-100 pb-2.5 last:border-0 last:pb-0">
                    <dt className="text-xs text-foreground-400">{row.label}</dt>
                    <dd className="text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg bg-primary-50 p-5">
              <div className="flex items-center gap-2 text-sm font-medium text-primary-800">
                <i className="ri-information-line text-base" />
                演示说明
              </div>
              <p className="mt-2.5 text-xs leading-relaxed text-primary-700/80">
                本站为影视内容发现与资料查询的演示站点，不提供在线播放。站内片名、演职员与评分均为示例数据，分享按钮仅做前端交互反馈。
              </p>
              <Link
                to="/about"
                className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-700 hover:text-accent-600"
              >
                了解站点定位
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 p-5">
              <h3 className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-compass-3-line text-accent-500" />
                继续发现
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/category/latest"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
                >
                  最新上线
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
                <Link
                  to="/category/hot"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
                >
                  热门榜单
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
                <Link
                  to="/special/special-02"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200"
                >
                  悬疑专题
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}